import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, Filter } from 'lucide-react';
import type { DashboardRow } from '@/shared/types';
import { Card } from '@/shared/ui/card/Card';
import { Button } from '@/shared/ui/button/Button';
import { cn, formatCurrency, formatNumber } from '@/shared/lib/utils';
import {
  extractPeriodColumns,
  extractSeries,
  getCuentaFormat,
  trendColors,
} from './pnlUtils';

interface PnLTrendsChartProps {
  rows: DashboardRow[];
}

/** Cuentas sugeridas por categoría de formato. */
const defaultAccounts: Record<string, string[]> = {
  currency: [
    'VENTAS_NETAS',
    'UTILIDAD_BRUTA',
    'MARGEN_CONTRIBUCIÓN',
    'EBITDA',
    'UTILIDAD NETA',
  ],
  number: ['TOTAL_VOLUMENES'],
  ratio: ['COLON_KG_PROMEDIO', 'VALOR_AGREGADO_TOTAL'],
};

export function PnLTrendsChart({ rows }: PnLTrendsChartProps) {
  const periodColumns = useMemo(() => extractPeriodColumns(rows), [rows]);

  // Catálogo de cuentas disponibles (que tengan al menos un valor no nulo).
  const availableAccounts = useMemo(() => {
    return rows
      .map(r => r.Cuenta)
      .filter((cuenta, idx, arr) => arr.indexOf(cuenta) === idx)
      .filter(cuenta => {
        const series = extractSeries(rows, cuenta, periodColumns);
        return series.some(p => p.value !== null && p.value !== 0);
      })
      .sort();
  }, [rows, periodColumns]);

  // Preselección: primeras cuentas de moneda disponibles.
  const initialSelection = useMemo(() => {
    const prefs = defaultAccounts.currency;
    return prefs.filter(p => availableAccounts.includes(p)).slice(0, 3);
  }, [availableAccounts]);

  const [selectedAccounts, setSelectedAccounts] = useState<string[]>(initialSelection);

  // Resetear selección cuando cambian las cuentas disponibles.
  useMemo(() => {
    if (selectedAccounts.length === 0 && initialSelection.length > 0) {
      setSelectedAccounts(initialSelection);
    }
  }, [initialSelection, selectedAccounts.length]);

  const toggleAccount = (cuenta: string) => {
    setSelectedAccounts(prev =>
      prev.includes(cuenta)
        ? prev.filter(a => a !== cuenta)
        : prev.length < 5
          ? [...prev, cuenta]
          : prev
    );
  };

  const resetSelection = () => setSelectedAccounts(initialSelection);

  // Datos combinados para Recharts: cada punto es { month, [cuenta]: value }.
  const chartData = useMemo(() => {
    const realColumns = periodColumns.filter(c => !c.isBudget && c.date);
    return realColumns.map(col => {
      const point: Record<string, number | string | null> = { month: col.yearMonth };
      selectedAccounts.forEach(cuenta => {
        const series = extractSeries(rows, cuenta, periodColumns);
        const item = series.find(s => s.month === col.yearMonth);
        point[cuenta] = item?.value ?? null;
      });
      return point;
    });
  }, [rows, periodColumns, selectedAccounts]);

  const leftAccounts = selectedAccounts.filter(
    a => getCuentaFormat(a) === 'currency'
  );
  const rightAccounts = selectedAccounts.filter(
    a => getCuentaFormat(a) !== 'currency'
  );

  const hasData = chartData.length > 0 && selectedAccounts.length > 0;

  return (
    <Card className="space-y-4 p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-text-primary flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Tendencias históricas
          </h3>
          <p className="text-sm text-text-secondary">
            Evolución mensual de las métricas seleccionadas.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={resetSelection}>
          Restablecer
        </Button>
      </div>

      {/* Selector de cuentas */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
          <Filter className="h-3.5 w-3.5" />
          Métricas (máx. 5)
        </div>
        <div className="flex flex-wrap gap-2">
          {availableAccounts.map(cuenta => {
            const isSelected = selectedAccounts.includes(cuenta);
            const format = getCuentaFormat(cuenta);
            return (
              <button
                key={cuenta}
                type="button"
                onClick={() => toggleAccount(cuenta)}
                disabled={!isSelected && selectedAccounts.length >= 5}
                className={cn(
                  'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                  isSelected
                    ? 'bg-primary text-white'
                    : 'bg-surface-elevated text-text-secondary hover:bg-surface-elevated/80 disabled:opacity-40'
                )}
                title={format === 'currency' ? 'Moneda' : format === 'number' ? 'Volumen' : 'Ratio'}
              >
                {cuenta}
              </button>
            );
          })}
        </div>
      </div>

      {/* Gráfico */}
      <div className="h-[360px] w-full">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11 }}
                angle={-35}
                textAnchor="end"
                height={55}
              />
              {leftAccounts.length > 0 && (
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  tick={{ fontSize: 11 }}
                  tickFormatter={value =>
                    typeof value === 'number' ? formatCurrency(value).replace('₡', '') : String(value)
                  }
                  width={70}
                />
              )}
              {rightAccounts.length > 0 && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  tickFormatter={value =>
                    typeof value === 'number' ? formatNumber(value, 2) : String(value)
                  }
                  width={60}
                />
              )}
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="rounded-xl border border-border bg-surface p-3 shadow-elevated">
                      <p className="mb-2 text-xs font-semibold text-text-primary">{label}</p>
                      <div className="space-y-1">
                        {payload.map((entry, idx) => {
                          const value = entry.value as number | null;
                          const format = getCuentaFormat(entry.dataKey as string);
                          const formatted =
                            value === null || value === undefined
                              ? '—'
                              : format === 'currency'
                                ? formatCurrency(value)
                                : formatNumber(value, format === 'ratio' ? 4 : 2);
                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between gap-4 text-xs"
                            >
                              <span className="flex items-center gap-1.5 text-text-secondary">
                                <span
                                  className="h-2 w-2 rounded-full"
                                  style={{ backgroundColor: entry.color }}
                                />
                                {entry.dataKey}
                              </span>
                              <span className="font-medium text-text-primary">{formatted}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                formatter={value => <span className="text-text-secondary">{value}</span>}
              />
              {selectedAccounts.map((cuenta, idx) => {
                const isRight = getCuentaFormat(cuenta) !== 'currency';
                return (
                  <Line
                    key={cuenta}
                    type="monotone"
                    dataKey={cuenta}
                    name={cuenta}
                    yAxisId={isRight ? 'right' : 'left'}
                    stroke={trendColors[idx % trendColors.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-border bg-surface-elevated/50">
            <p className="text-sm text-text-muted">
              Selecciona al menos una métrica para visualizar la tendencia.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
